import { z } from 'zod';
import { uuidSchema, LoginTypeSchema } from './common.schema.js';

/**
 * Cognito User Schemas — used by the Onboarding API to manage
 * users directly in AWS Cognito User Pool (no database).
 */

export const CognitoUserRoleSchema = z.enum([
  'TECHNICAL_ADMIN',
  'CLINICAL_ADMIN',
  'NICU_MANAGER',
  'NURSE',
  'PREP_TECHNICIAN',
  'DONOR',
  'DEFAULT',
  'ANCILLARY_STAFF',
  'LACTATION',
  'ENTERPRISE_ADMIN',
  'MILK_PREP_TECHNICIAN',
  'INVENTORY_MANAGER',
  'AUDITOR',
  'UNIT_SECRETARY',
  'MILK_BANK_OPERATOR',
  'DONOR_COORDINATOR',
  'DIETITIAN',
  'INTAKE_STAFF',
  // ── Control-plane roles (applicable_scope: CONTROLPLANE) ─────────────────
  'ADMIN',
  'PRODUCTION_SUPPORT',
  'PRODUCT_TEAM',
]);
export type CognitoUserRole = z.infer<typeof CognitoUserRoleSchema>;

export const MfaMethodSchema = z.enum(['SMS', 'TOTP']).optional();
export type MfaMethod = z.infer<typeof MfaMethodSchema>;

export const CreateCognitoUserBaseObjectSchema = z.object({
  email: z
    .string()
    .trim()
    .min(1, 'Email is required')
    .email('Invalid email address')
    .regex(/^[^"\\]*$/, 'Email must not contain double-quotes or backslashes'),
  first_name: z.string().min(1, 'First name is required').max(255),
  last_name: z.string().min(1, 'Last name is required').max(255),
  tenant_id: uuidSchema,
  hospital_id: uuidSchema,
  role: z
    .array(CognitoUserRoleSchema)
    .min(1, 'At least one role is required')
    .refine((roles) => new Set(roles).size === roles.length, { message: 'Duplicate roles are not allowed' })
    .default(['NURSE']),
  job_title: z.string().max(255).optional(),
  department: z.string().max(255).optional(),
  employee_number: z.string().max(100).optional(),
  phone_number: z
    .string()
    .regex(/^\+[1-9]\d{1,14}$/, 'Phone number must be in E.164 format (e.g. +1234567890)')
    .nullable()
    .optional(),
  mfa_method: MfaMethodSchema,
  location_id: uuidSchema.optional(),
  care_unit_id: uuidSchema.optional(),
  loginType: LoginTypeSchema,
  person_id: uuidSchema.optional(),
});

const smsMfaPhoneRefinement = {
  check: (data: { mfa_method?: string; phone_number?: string | null }) =>
    data.mfa_method !== 'SMS' || (data.phone_number != null && data.phone_number.length > 0),
  message: 'Phone number is required when MFA method is SMS',
  path: ['phone_number'] as string[],
};

export const CreateCognitoUserRequestSchema = CreateCognitoUserBaseObjectSchema
  .refine(smsMfaPhoneRefinement.check, {
    message: smsMfaPhoneRefinement.message,
    path: smsMfaPhoneRefinement.path,
  })
  .refine(
    (data) => data.loginType === 'donor' || !data.person_id,
    { message: 'person_id is only allowed when loginType is donor', path: ['person_id'] },
  );

export const TenantSeedUserSchema = CreateCognitoUserBaseObjectSchema
  .omit({ tenant_id: true, hospital_id: true })
  .refine(smsMfaPhoneRefinement.check, {
    message: smsMfaPhoneRefinement.message,
    path: smsMfaPhoneRefinement.path,
  });

export type TenantSeedUser = z.infer<typeof TenantSeedUserSchema>;

export type CreateCognitoUserRequest = z.infer<typeof CreateCognitoUserRequestSchema>;

export const CognitoUserResponseSchema = z.object({
  sub: z.string(),
  username: z.string(),
  email: z.string().email(),
  first_name: z.string(),
  last_name: z.string(),
  status: z.string(),
  enabled: z.boolean().optional(),
  tenant_id: uuidSchema,
  hospital_id: uuidSchema,
  role: z.array(CognitoUserRoleSchema),
  created_at: z.string(),
  job_title: z.string().optional(),
  department: z.string().optional(),
  employee_number: z.string().optional(),
  phone_number: z.string().nullable().optional(),
  location_id: uuidSchema.optional(),
  care_unit_id: uuidSchema.optional(),
  temporary_password: z.string().optional(),
  person_id: uuidSchema.optional(),
});
export type CognitoUserResponse = z.infer<typeof CognitoUserResponseSchema>;

// ── Settable Status (used by PATCH to enable/disable a user) ─────────────────

export const CognitoSettableStatusSchema = z.enum(['CONFIRMED', 'DISABLED']);
export type CognitoSettableStatus = z.infer<typeof CognitoSettableStatusSchema>;

// ── Update User ───────────────────────────────────────────────────────────────

export const UpdateCognitoUserRequestSchema = z.object({
  first_name: z.string().min(1).max(255).optional(),
  last_name: z.string().min(1).max(255).optional(),
  role: z
    .array(CognitoUserRoleSchema)
    .min(1, 'At least one role is required')
    .refine((roles) => new Set(roles).size === roles.length, { message: 'Duplicate roles are not allowed' })
    .optional(),
  status: CognitoSettableStatusSchema.optional(),
  enabled: z.boolean().optional(),
  job_title: z.string().max(255).optional(),
  department: z.string().max(255).optional(),
  employee_number: z.string().max(100).optional(),
  phone_number: z
    .string()
    .regex(/^\+[1-9]\d{1,14}$/, 'Phone number must be in E.164 format (e.g. +1234567890)')
    .nullable()
    .optional(),
  location_id: uuidSchema.optional(),
  care_unit_id: uuidSchema.optional(),
});
export type UpdateCognitoUserRequest = z.infer<typeof UpdateCognitoUserRequestSchema>;

// ── Cognito User Status (returned by Cognito, used for filtering) ─────────────

export const CognitoUserStatusSchema = z.enum([
  'UNCONFIRMED',
  'CONFIRMED',
  'ARCHIVED',
  'COMPROMISED',
  'UNKNOWN',
  'RESET_REQUIRED',
  'FORCE_CHANGE_PASSWORD',
  'EXTERNAL_PROVIDER',
]);
export type CognitoUserStatus = z.infer<typeof CognitoUserStatusSchema>;

// ── List Users Query ──────────────────────────────────────────────────────────

export const ListCognitoUsersQuerySchema = z.object({
  tenant_id: uuidSchema,
  limit: z.coerce.number().int().min(1).max(60).default(25),
  pagination_token: z.string().optional(),
  loginType: LoginTypeSchema,
  apptype: z.enum(['CONTROLPLANE']).optional(),
});
export type ListCognitoUsersQuery = z.infer<typeof ListCognitoUsersQuerySchema>;

// ── List Users Response ───────────────────────────────────────────────────────

export const ListCognitoUsersResponseSchema = z.object({
  users: z.array(CognitoUserResponseSchema),
  pagination_token: z.string().optional(),
});
export type ListCognitoUsersResponse = z.infer<typeof ListCognitoUsersResponseSchema>;

// ── Attribute-Based Search ────────────────────────────────────────────────────

/**
 * Attributes accepted by the attribute-based search endpoint.
 *
 * **Server-side filtering (Cognito `ListUsers` `Filter` parameter)**
 * Only standard attributes are supported by the Cognito API:
 *   `email`, `given_name`, `family_name`, `phone_number`, `sub`,
 *   `cognito:user_status`
 *
 * **Client-side filtering (service layer)**
 * `custom:*` attributes cannot be used in the Cognito `ListUsers` `Filter`
 * parameter. When a `custom:*` attribute is requested, the service must
 * paginate through all matching users and filter the results in-process.
 */
export const CognitoSearchableAttributeSchema = z.enum([
  'email',
  'given_name',
  'family_name',
  'phone_number',
  'sub',
  'cognito:user_status',
  'custom:tenant_id',
  'custom:hospital_id',
  'custom:primary_hospital_id',
  'custom:role_code',
  'custom:job_title',
  'custom:department',
  'custom:employee_number',
  'custom:person_id',
]);
export type CognitoSearchableAttribute = z.infer<typeof CognitoSearchableAttributeSchema>;

export const AttributeBasedSearchQuerySchema = z
  .object({
    tenant_id: uuidSchema,
    loginType: LoginTypeSchema,
    username: z.string().min(1).optional(),
    attribute: z
      .preprocess(
        (val) => {
          if (val === undefined || val === null) return undefined;
          return Array.isArray(val) ? val : [val];
        },
        z.array(CognitoSearchableAttributeSchema).min(1).optional(),
      )
      .optional(),
    /**
     * Number of results per page (1–60, default 25).
     * Only honoured for standard-attribute searches (non-`custom:*` primary
     * attribute). Custom-attribute searches always exhaust all pages
     * internally and ignore this field.
     */
    limit: z.coerce.number().int().min(1).max(60).default(25).optional(),
    /**
     * Opaque token returned by a previous response to fetch the next page.
     * Only applies to standard-attribute searches. Ignored for
     * `username`-based lookups and `custom:*` attribute searches (which
     * always return the full result set).
     */
    pagination_token: z.string().optional(),
    // Each entry in `value` corresponds positionally to the same-index entry in `attribute`.
    // e.g., ?attribute=email&value=a@b.com&attribute=custom:role_code&value=NURSE
    value: z
      .preprocess(
        (val) => {
          if (val === undefined || val === null) return undefined;
          return Array.isArray(val) ? val : [val];
        },
        z.array(z.string().min(1)).min(1).optional(),
      )
      .optional(),
    /** Pass 'CONTROLPLANE' to apply control-plane role fallback logic. */
    apptype: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    const hasUsername = !!data.username;
    const hasAttribute = !!(data.attribute && data.attribute.length > 0);
    const hasValue = !!(data.value && data.value.length > 0);

    if (!hasUsername && !hasAttribute) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Provide either username or attribute + value pairs to search',
      });
      return;
    }
    if (hasUsername && hasAttribute) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Provide either username or attribute + value, not both',
      });
      return;
    }
    if (hasAttribute && !hasValue) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'value is required when attribute is provided',
        path: ['value'],
      });
      return;
    }
    if (hasValue && !hasAttribute) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'attribute is required when value is provided',
        path: ['attribute'],
      });
      return;
    }
    if (hasAttribute && hasValue && data.attribute!.length !== data.value!.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `attribute and value counts must match — got ${data.attribute!.length} attribute(s) and ${data.value!.length} value(s)`,
      });
    }
  });
export type AttributeBasedSearchQuery = z.infer<typeof AttributeBasedSearchQuerySchema>;

export const AttributeBasedSearchResponseSchema = z.object({
  users: z.array(CognitoUserResponseSchema.extend({ role: z.array(z.string()) })),
  pagination_token: z.string().optional(),
});
export type AttributeBasedSearchResponse = z.infer<typeof AttributeBasedSearchResponseSchema>;

// ── Reset Temporary Password ──────────────────────────────────────────────────

export const ResetTempPasswordRequestSchema = z.object({
  tenant_id: uuidSchema,
  loginType: LoginTypeSchema,
});
export type ResetTempPasswordRequest = z.infer<typeof ResetTempPasswordRequestSchema>;

export const ResetTempPasswordResponseSchema = z.object({
  message: z.string(),
});
export type ResetTempPasswordResponse = z.infer<typeof ResetTempPasswordResponseSchema>;

// ── Forgot Password ───────────────────────────────────────────────────────────

export const ForgotPasswordRequestSchema = z.object({
  email: z
    .string()
    .trim()
    .min(1, 'Email is required')
    .email('Invalid email address')
    .regex(/^[^"\\]*$/, 'Email must not contain double-quotes or backslashes'),
});
export type ForgotPasswordRequest = z.infer<typeof ForgotPasswordRequestSchema>;

export const ForgotPasswordResponseSchema = z.object({
  message: z.string(),
  email: z.string().email(),
});
export type ForgotPasswordResponse = z.infer<typeof ForgotPasswordResponseSchema>;
